/**
 * Copyright 2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCreateCustomerMutation } from "~/generated/graphql";
import { showError, showSuccess } from "~/components";
import { CustomerFormSchemaType } from "~/views/customers/components/Form/schema";

type CreateCustomerProps = {
  onSuccess: () => void;
  onError: () => void;
};

export function useCreateCustomer({ onSuccess, onError }: CreateCustomerProps) {
  const [createCustomerMutation, { loading: isLoading }] =
    useCreateCustomerMutation();

  function createCustomer(data: CustomerFormSchemaType) {
    return createCustomerMutation({
      variables: {
        input: {
          name: data.name,
          backupPoliciesBoundary: {
            minRPOInHours: data.rpo,
            maxPrimarySnapshotRetentionQuantity:
              data.primarySnapshotsRetentionQuantity,
            maxPrimarySnapshotRetentionInDays:
              data.primarySnapshotsRetentionInDays,
            maxReplicaSnapshotRetentionQuantity:
              data.replicaSnapshotsRetentionQuantity,
            maxReplicaSnapshotRetentionInDays:
              data.replicaSnapshotsRetentionInDays,
          },
        },
      },
    })
      .then(() => {
        showSuccess("Customer has been created.");
        onSuccess();
        return null;
      })
      .catch(() => {
        showError("Something went wrong.");
        onError();
      });
  }

  return [createCustomer, isLoading] as const;
}
